/**
 * # VanillaReaderEventBus
 *
 *  ## RESPONSIBILITIES
 *
 * The VanillaReaderEventBus is the application's communication channel. It lets any of our components
 * dispatch and add listeners for CustomEvents (https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Creating_and_triggering_events).
 *
 * The actual element that acts as the "dispatch central" for the event bus is by default the "<body>" element in index.html.
 *
 * Any component that you write "on top" of the VanillaReader will need to include this file as it is the common "protocol"
 * and channel for communicating between the components.
 *
 * ## EVENT TYPES
 *
 * This file contains `VanillaReaderAppEvents` which holds all the event names used by the VanillaReader. It also
 * contains all the various `IVanillaReaderAppEvent` types that specify what data is sent along with each event.
 *
 * ## NOTES:
 *
 * The naming convention for events are as follows:
 *
 * - An "INTENT" is dispatched to trigger an action (typically sent from the UI)
 * - Any other event is sent **after** an action has completed and reflects the changed state.
 *
 * The exception to this convention is the "APP_UNLOADING" event which fires as part of the browser `window`'s unload process.
 *
 */
import {IVanillaReaderAppEventTypeMap} from './IVanillaReaderAppEventTypeMap';

export class VanillaReaderEventBus {
    static eventBusElement: Element | undefined;
    private static _listeners: Map<keyof IVanillaReaderAppEventTypeMap, Set<CustomEventListener<any>>> = new Map();

    private constructor() {}

    static addEventListener<T extends keyof IVanillaReaderAppEventTypeMap>(
        event: T, listener: CustomEventListener<IVanillaReaderAppEventTypeMap[T]>, once: boolean = false,
    ): void {
        VanillaReaderEventBus._getBus().addEventListener(event, listener as EventListener, {
            once,
        });

        /* If the `once` parameter is set to true, the DOM API will take care of the event listener life cycle
        *  so we should not save it to our own `listeners` set.
        */
        if (!once) {
            let listeners = VanillaReaderEventBus._listeners.get(event);
            if (!listeners) {
                listeners = new Set();
                VanillaReaderEventBus._listeners.set(event, listeners);
            }
            listeners.add(listener);
        }
    }

    static destroy(): void {
        VanillaReaderEventBus._listeners.forEach((listeners, eventType) => {
            listeners.forEach(listener => {
                VanillaReaderEventBus._getBus().removeEventListener(eventType, listener as EventListener);
            });
        });
        VanillaReaderEventBus._listeners.clear();
    }

    static dispatchEvent<T extends keyof IVanillaReaderAppEventTypeMap>(event: CustomEvent<IVanillaReaderAppEventTypeMap[T]>): void {
        console.log(`<<< Colibrio (${event.type})`, event.detail);
        try {
            window.parent.postMessage({type: event.type, value: event.detail}, "*");
        } catch (e){
            console.warn(e);
        }
        VanillaReaderEventBus._getBus().dispatchEvent(event);
    }

    static removeEventListener<T extends keyof IVanillaReaderAppEventTypeMap>(
        eventType: T, listener: CustomEventListener<IVanillaReaderAppEventTypeMap[T]>,
    ): void {
        VanillaReaderEventBus._getBus().removeEventListener(eventType, listener as EventListener);
        let listeners = VanillaReaderEventBus._listeners.get(eventType);
        if (listeners) {
            listeners.delete(listener);
        }
    }

    private static _getBus(): Element {
        if (!VanillaReaderEventBus.eventBusElement) {
            VanillaReaderEventBus.eventBusElement = document.documentElement;
        }

        return VanillaReaderEventBus.eventBusElement;
    }

    static createEvent<T extends keyof IVanillaReaderAppEventTypeMap>(
        type: T, detail: IVanillaReaderAppEventTypeMap[T]): CustomEvent<IVanillaReaderAppEventTypeMap[T]> {
        return new CustomEvent<IVanillaReaderAppEventTypeMap[T]>(type, {detail});
    }
}

export type CustomEventListener<T> = (evt: CustomEvent<T>) => void;
